'use client';

import { useRef, useState, useCallback, useEffect } from 'react';

/**
 * Drag-to-scroll + wheel-to-scroll for horizontal carousels.
 *
 * Shared across the dashboard `ConceptGrid` carousels and the project-page
 * `ScrollableCarousel` so swipe behaviour (mouse drag on desktop, native touch
 * on mobile, vertical-wheel → horizontal scroll) stays identical everywhere.
 *
 * `hasDragged` lets the consumer suppress click-through after a drag, and
 * `onDragStart` preventDefault kills the browser's native image/link drag so a
 * grabbed thumbnail doesn't hijack the gesture.
 */
export function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;
    setIsMouseDown(true);
    setHasDragged(false);
    setStartX(e.pageX - ref.current.offsetLeft);
    setScrollLeft(ref.current.scrollLeft);
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isMouseDown || !ref.current) return;
      const x = e.pageX - ref.current.offsetLeft;
      const diff = Math.abs(x - startX);
      // Only start dragging after moving more than 5px
      if (diff > 5) {
        setHasDragged(true);
        e.preventDefault();
        const walk = (x - startX) * 1.5;
        ref.current.scrollLeft = scrollLeft - walk;
      }
    },
    [isMouseDown, startX, scrollLeft]
  );

  const onMouseUp = useCallback(() => {
    setIsMouseDown(false);
    // Reset hasDragged after a short delay to allow click to process
    setTimeout(() => setHasDragged(false), 0);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsMouseDown(false);
    setHasDragged(false);
  }, []);

  // Prevent native browser drag on images and links
  const onDragStart = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Wheel scroll: convert vertical wheel to horizontal scroll
  // Using useEffect with { passive: false } to allow preventDefault
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle if container can scroll horizontally
      const { scrollWidth, clientWidth } = element;
      if (scrollWidth <= clientWidth) return;

      // Convert vertical wheel to horizontal scroll
      if (e.deltaY !== 0) {
        e.preventDefault();
        element.scrollLeft += e.deltaY;
      }
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, []);

  return {
    ref,
    isMouseDown,
    hasDragged,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave,
      onDragStart,
    },
  };
}
